import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantsApi } from '@/api/tenants';
import { groupsApi } from '@/api/groups';
import { invalidateContentQueries } from '@/utils/invalidateContent';
import { useChat } from '@/store/chat';

// 团队空间=第三作用域：激活态由服务端 user.tenant_id 驱动，
// 前端以 /tenants/me 的 is_active 行为准（activeTenant 非空=团队空间上下文）
export const useTenants = (enabled: boolean = true) => {
  const queryClient = useQueryClient();

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants', 'me'],
    queryFn: async () => (await tenantsApi.list()).data,
    enabled,
    staleTime: 60 * 1000,
  });

  const activeTenant = tenants?.find((t) => t.is_active) || null;

  // 切换空间改变所有内容端点的返回口径：租户状态 + 内容查询全部失效
  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
    invalidateContentQueries(queryClient);
    // chat 会话按空间隔离后，persist 的 psb-chat 没有空间维度：切空间会拿旧空间的
    // activeConversationId 回拉详情（后端 404 自愈但闪一瞬），切空间直接清空
    useChat.getState().setActiveConversationId(null);
  };

  const createMutation = useMutation({
    mutationFn: (name: string) => tenantsApi.create(name),
    // 建空间即激活（服务端口径），同样全量失效
    onSuccess: invalidateAll,
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => tenantsApi.activate(id),
    onSuccess: invalidateAll,
  });

  const deactivateMutation = useMutation({
    mutationFn: () => tenantsApi.deactivate(),
    onSuccess: invalidateAll,
  });

  return {
    tenants,
    activeTenant,
    isLoading,
    createTenant: createMutation.mutateAsync,
    activateTenant: activateMutation.mutateAsync,
    deactivateTenant: deactivateMutation.mutateAsync,
    isSwitching: activateMutation.isPending || deactivateMutation.isPending || createMutation.isPending,
  };
};

export const useTenantMembers = (tenantId?: string) => {
  const queryClient = useQueryClient();

  const { data: members, isLoading } = useQuery({
    queryKey: ['tenants', tenantId, 'members'],
    queryFn: async () => (await tenantsApi.listMembers(tenantId!)).data,
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });

  // 成员增减影响席位计数（/tenants/me 的 member_count），两个 key 一起失效
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenants'] });

  const addMutation = useMutation({
    mutationFn: (data: { email: string; role: string }) => tenantsApi.addMember(tenantId!, data),
    onSuccess: invalidate,
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => tenantsApi.removeMember(tenantId!, userId),
    onSuccess: invalidate,
  });

  return {
    members,
    isLoading,
    addMember: addMutation.mutateAsync,
    removeMember: removeMutation.mutateAsync,
    isAdding: addMutation.isPending,
  };
};

// 租户审计流水（只读；owner/admin 才能看，由调用方按 my_role 控制 enabled）
export const useTenantAudit = (tenantId?: string, enabled: boolean = true, limit: number = 50) => {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['tenants', tenantId, 'audit', limit],
    queryFn: async () => (await tenantsApi.listAudit(tenantId!, limit)).data,
    enabled: !!tenantId && enabled,
    staleTime: 30 * 1000,
  });
  return { entries, isLoading };
};

// 租户分组（部门雏形）：服务端按激活租户口径，前端仅在 activeTenant 非空时拉取
export const useGroups = (tenantId?: string) => {
  const queryClient = useQueryClient();

  const { data: groups, isLoading } = useQuery({
    queryKey: ['tenants', tenantId, 'groups'],
    queryFn: () => groupsApi.list(),
    enabled: !!tenantId,
    staleTime: 30 * 1000,
  });

  // 分组增删改与成员增减都影响列表（member_count），整组 key 失效
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenants', tenantId, 'groups'] });

  const createMutation = useMutation({
    mutationFn: (name: string) => groupsApi.create(name),
    onSuccess: invalidate,
  });
  const renameMutation = useMutation({
    mutationFn: ({ groupId, name }: { groupId: string; name: string }) => groupsApi.rename(groupId, name),
    onSuccess: invalidate,
  });
  const removeMutation = useMutation({
    mutationFn: (groupId: string) => groupsApi.remove(groupId),
    onSuccess: invalidate,
  });
  const addMemberMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) => groupsApi.addMember(groupId, userId),
    onSuccess: invalidate,
  });
  const removeMemberMutation = useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) => groupsApi.removeMember(groupId, userId),
    onSuccess: invalidate,
  });

  return {
    groups,
    isLoading,
    createGroup: createMutation.mutateAsync,
    renameGroup: renameMutation.mutateAsync,
    removeGroup: removeMutation.mutateAsync,
    addGroupMember: addMemberMutation.mutateAsync,
    removeGroupMember: removeMemberMutation.mutateAsync,
    isCreating: createMutation.isPending,
  };
};

// 单个分组的成员清单（展开时拉取）
export const useGroupMembers = (tenantId?: string, groupId?: string) => {
  const { data: members, isLoading } = useQuery({
    queryKey: ['tenants', tenantId, 'groups', groupId, 'members'],
    queryFn: () => groupsApi.listMembers(groupId!),
    enabled: !!tenantId && !!groupId,
    staleTime: 30 * 1000,
  });
  return { members, isLoading };
};
